import { React, useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import {
  PlusIcon, TrashIcon, CalendarDaysIcon, FunnelIcon, XMarkIcon,
  BookmarkIcon, ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import SearchButton from './SearchButton';
import DateRangePicker from './DateRangePicker';
import ConfirmationModal from './ConfirmationModal';
import { useProject } from '../../config/ProjectContext';
// import useIsDevicePermissionAllowed from '../../hooks/useIsDevicePermissionAllowed';
import useFilterManager from '../../hooks/useFilterManager';

const operators = [
  { value: 'EQ', label: 'Is equal' },
  { value: 'NE', label: 'Is not equal' },
  { value: 'LT', label: 'Is lower than ' },
  { value: 'LE', label: 'Is lower or equal than' },
  { value: 'GT', label: 'Is greater than' },
  { value: 'GE', label: 'Is greater or equal than' },
  { value: 'IN', label: 'Is in' },
];

const booleanOperators = operators.filter((o) => o.value === 'EQ' || o.value === 'NE');

const stringOperators = operators.filter((o) => o.value === 'EQ' || o.value === 'NE' || o.value === 'IN');

const strictStringEqualOperators = operators.filter((o) => o.value === 'EQ');

const dateOperators = operators.filter((o) => o.value !== 'IN');

export const valueTypes = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  BOOLEAN: 'BOOLEAN',
  LIST: 'LIST',
  STRICT_STRING_EQUAL: 'STRICT_STRING_EQUAL',
};

export default function JSONBuilderFilters({
  setSelectedFilters,
  selectedStartDate, setSelectedStartDate, selectedStartTime,
  setSelectedStartTime, selectedEndDate, setSelectedEndDate,
  setSelectedEndTime, selectedEndTime, id,
  datesOptional, messageTypeVisible,
  filtersVisible, deviceSerialVisible,
  linkVisible, linkText, linkURL,
  deviceSerial, setDeviceSerial,
  messageType, setMessageType,
  deviceSerialError, maxDaysBetweenDates, subtitle,
  filtersComponents, jsonColumns, setDateRangePickerVisibility, setFilterError,
  filterError, onSearch, isEndDateEnabled, showNoEndateCheckbox,
  useProcessedTimestamp, setUseProcessedTimestamp, showTimestampToggle
}) {


  // const [deviceToCheck, setDeviceToCheck] = useState(null);
  // const [fieldToAddInTheFilter, setFieldToAddInTheFilter] = useState(null);
  // const { isAllowed: isDeviceAllowed, isLoading: isLoadingDeviceAllowed } = useIsDevicePermissionAllowed(deviceToCheck);
  const [filters, setFilters] = useState([]);
  const [inputFilters, setInputFilters] = useState([
    {
      column: '', operator: '', value: '', id: 0,
    },
  ]);
  const [selectorError, setSelectorError] = useState('');
  const [columnsToSelect, setColumnsToSelect] = useState(jsonColumns);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupRef = useRef(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const { project } = useProject();
  const projectId = project?.code;

  const {
    savedFilters,
    saveFilter,
    deleteFilter: deleteSavedFilterFromManager,
  } = useFilterManager(projectId, id);

  const [newFilterDescription, setNewFilterDescription] = useState('');
  const [showSaveFilterInput, setShowSaveFilterInput] = useState(false);
  const [savedFiltersSearchTerm, setSavedFiltersSearchTerm] = useState('');
  const [newFilterNotes, setNewFilterNotes] = useState('');
  const [showFilterAddedMessage, setShowFilterAddedMessage] = useState(false);
  const [modalTitle, setModalTitle] = useState('Apply Filters?');
  const [modalMessage, setModalMessage] = useState('You have active filters. Do you want to apply them before closing?');
  const [modalConfirmText, setModalConfirmText] = useState('Apply & Close');
  const [modalCancelText, setModalCancelText] = useState('Close Without Applying');

  const filterIsEmpty = (filter) => filter.column === '' || filter.operator === '' || filter.value === '';

  const hasCompleteUnaddedFilter = () => {
    const lastInputFilter = inputFilters[inputFilters.length - 1];
    if (!lastInputFilter) return false;

    const isComplete = lastInputFilter.column && lastInputFilter.column !== '' &&
      lastInputFilter.operator && lastInputFilter.operator !== '' &&
      lastInputFilter.value !== '' && lastInputFilter.value != null;

    if (!isComplete) return false;

    // Check if this filter is already in the applied filters
    const alreadyInConfirmedFilters = filters.some(f =>
      f.column === lastInputFilter.column &&
      f.operator === lastInputFilter.operator &&
      f.value === lastInputFilter.value
    );

    return !alreadyInConfirmedFilters;
  };

  const addNewEmptyFilter = () => {
    // add a new empty filter only if the last filter is not empty
    const lastInputFilter = inputFilters[inputFilters.length - 1];
    const isLastFilterComplete = lastInputFilter &&
      lastInputFilter.column && lastInputFilter.column !== '' &&
      lastInputFilter.operator && lastInputFilter.operator !== '' &&
      lastInputFilter.value !== '' && lastInputFilter.value != null;
    if (isLastFilterComplete) {
      setInputFilters((prevFilters) => [
        ...prevFilters,
        {
          column: '', operator: '', value: '', id: uuidv4(),
        },
      ]);
    }
  };


  const addFilter = (filter) => {
    console.log('Adding filter:', filter);
    setFilterError(undefined);
    // setFieldToAddInTheFilter(filter.column);
    if (filterIsEmpty(filter)) {
      return;
    }

    // trim the end of the value to avoid issues with leading/trailing spaces
    let processedValue = filter.value.trimEnd();

    // If operator is IN, convert comma-separated string to array
    if (filter.operator === 'IN' && typeof processedValue === 'string') {
      processedValue = processedValue.split(',').map(val => val.trim()).filter(val => val !== '');
    }

    const processedFilter = { ...filter, value: processedValue };

    // if (['device_serial', 'serial'].includes(filter.column)) {
    //   setDeviceToCheck(filter.value);
    // } else {

    setFilters((prevFilters) => [...prevFilters, processedFilter]);

    // Show confirmation message
    setShowFilterAddedMessage(true);
    setTimeout(() => setShowFilterAddedMessage(false), 2000);

    // }

    addNewEmptyFilter();
  };

  // useEffect(() => {
  //   console.log('Checking device permission for:', deviceToCheck);
  //   if (isLoadingDeviceAllowed || !deviceToCheck) return;

  //   if (!isDeviceAllowed) {
  //     setFilterError('You do not have permission to access this device.');
  //     return;
  //   }

  //   if (!isDeviceAllowed) {
  //     setFilterError('You do not have permission to access this device.');
  //     return;
  //   }
  //   // Add the filter only when permission is granted
  //   setFilters((prevFilters) => [
  //     ...prevFilters,
  //     {
  //       column: fieldToAddInTheFilter,
  //       operator: 'EQ',
  //       value: deviceToCheck,
  //       id: uuidv4(),
  //     },
  //   ]);

  //   addNewEmptyFilter();
  // }, [
  //   isDeviceAllowed,
  //   isLoadingDeviceAllowed,
  //   deviceToCheck]);

  useEffect(() => {
    setSelectedFilters(
      filters.length > 0 ? JSON.stringify({
        filter: filters.map(({ column, operator, value }) => ({
          column,
          operator,
          value,
        })),
      }) : '',
    );
  }, [filters, setSelectedFilters]);

  const removeFilterCondition = (filter) => {
    setFilters((prevFilters) => prevFilters.filter((f) => f.column !== filter.column));
    setInputFilters((prevFilters) => prevFilters.filter((f) => f.id !== filter.id));
    setColumnsToSelect(
      columnsToSelect.map((column) => (column.id === filter.column
        ? { ...column, selected: false }
        : column)),
    );

    if (jsonColumns.find((c) => c.id === filter.column)?.isMandatory) {
      setColumnsToSelect(
        columnsToSelect.map((column) => (column.isMandatory
          ? { ...column, selected: true }
          : column)),
      );
    }
  };

  const updateFilter = (field, filterValue, inputFilter) => {
    if (inputFilter.maxValues && field === 'value' && filterValue.split(',').length > inputFilter.maxValues) {
      setSelectorError(`You can only select up to ${inputFilter.maxValues} values`);
    } else setSelectorError('');
    console.log('Updating setInputFilters');
    setInputFilters(
      inputFilters.map((filter, index) => (index === inputFilters.indexOf(inputFilter)
        ? {
          ...filter,
          [field]: filterValue,
        }
        : filter)),
    );
  };

  const onSubmit = (event, filtersToSubmitOverride = null) => {
    event.preventDefault();

    // if (isLoadingDeviceAllowed) {
    //   return;
    // }

    let finalFiltersForSubmission;

    if (filtersToSubmitOverride) {
      finalFiltersForSubmission = filtersToSubmitOverride;
    } else {
      // IMPORTANT: Do NOT auto-add incomplete filters when submitting
      // Only use the filters that have been explicitly added
      finalFiltersForSubmission = [...filters];
    }

    const createJsonFromFiltersLocal = (filtersToProcess) => filtersToProcess
      .filter(filter => filter &&
        filter.column && filter.column !== '' &&
        filter.operator && filter.operator !== '' &&
        filter.value !== '' && filter.value != null)
      .map((filter) => ({
        column: filter.column,
        operator: filter.operator,
        value: filter.value,
      }));

    const processedFiltersForJson = createJsonFromFiltersLocal(finalFiltersForSubmission);
    const jsonStringToSubmit = processedFiltersForJson.length > 0 ? JSON.stringify({ filter: processedFiltersForJson }, null, 2) : '';

    setSelectedFilters(jsonStringToSubmit);
    onSearch(event, jsonStringToSubmit);
  };

  useEffect(() => {
    const initialFilters = jsonColumns.filter((col) => col.isMandatory).map((col) => ({
      id: uuidv4(),
      column: col.id,
      operator: operators[0].value,
      value: '',
      isMandatory: col.isMandatory,
      maxValues: col.maxValues,
    }));
    if (initialFilters.length > 0) {
      setInputFilters(initialFilters);
      setColumnsToSelect(
        columnsToSelect.map((column) => (column.isMandatory
          ? { ...column, selected: true }
          : column)),
      );
    }
  }, []);

  useEffect(() => {
    if (inputFilters.length > 0) {
      const mandatoryFilters = inputFilters.filter((filter) => filter.isMandatory);
      if (mandatoryFilters.length > 0) {
        if (mandatoryFilters.some((filter) => filter.column === '' || filter.operator === '' || filter.value === '')) {
          setFilterError('The mandatory filters must been added');
        } else {
          setFilterError('');
        }
      }
    }
    if (inputFilters.length === 0) {
      addNewEmptyFilter();
    }
  }, [inputFilters, setFilterError]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        const hasUnadded = hasCompleteUnaddedFilter();
        if (filters.length > 0 || hasUnadded) {
          // Set appropriate message based on the situation
          if (hasUnadded && filters.length > 0) {
            setModalTitle('Apply Current Filters?');
            setModalMessage('You have active filters and a complete filter that hasn\'t been added yet. Only the current filters will be applied. The incomplete filter will be lost.');
            setModalConfirmText('Apply Current Filters');
            setModalCancelText('Close Without Applying');
          } else if (hasUnadded && filters.length === 0) {
            setModalTitle('Filter Not Added');
            setModalMessage('You have filled out a filter but haven\'t added it yet. This filter will be lost if you close now.');
            setModalConfirmText('Close');
            setModalCancelText('');
          } else {
            setModalTitle('Apply Filters?');
            setModalMessage('You have active filters. Do you want to apply them before closing?');
            setModalConfirmText('Apply & Close');
            setModalCancelText('Close Without Applying');
          }
          setIsConfirmationModalOpen(true);
        } else {
          setIsPopupOpen(false);
        }
      }
    }
    if (isPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopupOpen, popupRef, filters]);

  const handleConfirmApplyFilters = () => {
    // Only apply existing filters, don't auto-add the complete unadded filter
    onSubmit(new Event('submit', { bubbles: true, cancelable: true }), filters);
    setIsPopupOpen(false);
    setIsConfirmationModalOpen(false);
  };

  const handleModalConfirm = () => {
    const hasUnadded = hasCompleteUnaddedFilter();

    // If there's only an unadded filter and no applied filters, just close
    if (hasUnadded && filters.length === 0) {
      setIsPopupOpen(false);
      setIsConfirmationModalOpen(false);
      return;
    }

    // Apply only the current filters (don't auto-add the unadded one)
    handleConfirmApplyFilters();
  };

  const handleCancelApplyFilters = () => {
    setIsPopupOpen(false);
    setIsConfirmationModalOpen(false);
  };

  const handleClosePopup = () => {
    const hasUnadded = hasCompleteUnaddedFilter();
    if (filters.length > 0 || hasUnadded) {
      // Set appropriate message based on the situation
      if (hasUnadded && filters.length > 0) {
        setModalTitle('Apply Current Filters?');
        setModalMessage('You have active filters and a complete filter that hasn\'t been added yet. Only the current filters will be applied. The incomplete filter will be lost.');
        setModalConfirmText('Apply Current Filters');
        setModalCancelText('Close Without Applying');
      } else if (hasUnadded && filters.length === 0) {
        setModalTitle('Filter Not Added');
        setModalMessage('You have filled out a filter but haven\'t added it yet. This filter will be lost if you close now.');
        setModalConfirmText('Close');
        setModalCancelText('');
      } else {
        setModalTitle('Apply Filters?');
        setModalMessage('You have active filters. Do you want to apply them before closing?');
        setModalConfirmText('Apply & Close');
        setModalCancelText('Close Without Applying');
      }
      setIsConfirmationModalOpen(true);
    } else {
      setIsPopupOpen(false);
    }
  };

  const handleDateChange = (startDate, endDate) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const handleColumnChange = (event, targetInputFilterId) => {
    const newColumnId = event.target.value;
    const columnDef = jsonColumns.find((c) => c.id === newColumnId);

    setInputFilters(
      inputFilters.map((filterInMap) => {
        if (filterInMap.id === targetInputFilterId) {
          let newOperator = operators[0].value;
          if (columnDef) {
            if (columnDef.valueType === valueTypes.BOOLEAN) {
              newOperator = booleanOperators[0]?.value || operators[0].value;
            } else if (columnDef.valueType === valueTypes.STRING) {
              newOperator = stringOperators[0]?.value || operators[0].value;
            } else if (columnDef.valueType === valueTypes.STRICT_STRING_EQUAL) {
              newOperator = strictStringEqualOperators[0]?.value || operators[0].value;
            } else if (columnDef.valueType === valueTypes.DATE) {
              newOperator = dateOperators[0]?.value || operators[0].value;
            }
          }

          return {
            ...filterInMap,
            column: newColumnId,
            operator: newOperator,
            value: columnDef?.valueType === valueTypes.DATE && selectedStartDate && moment.isMoment(selectedStartDate)
              ? selectedStartDate.format('YYYY-MM-DD HH:mm:ss')
              : '',
          };
        }
        return filterInMap;
      }),
    );
  };

  const handleChangeDatePicker = (value, inputFilterId) => {
    if (value === null || value === '') {
      // Clear the filter value
      setInputFilters(
        inputFilters.map((filter) => (filter.id === inputFilterId
          ? {
            ...filter,
            value: '',
          }
          : filter)),
      );
    } else {
      // Convert the datetime-local string to the expected format
      const dateValue = moment(value).format('YYYY-MM-DD HH:mm:ss');

      setInputFilters(
        inputFilters.map((filter) => (filter.id === inputFilterId
          ? {
            ...filter,
            value: dateValue,
          }
          : filter)),
      );
    }
  };

  const handleTimestampToggleChange = (event) => {
    if (setUseProcessedTimestamp) {
      setUseProcessedTimestamp(event.target.checked);
    }
  };

  const handleSaveCurrentFilter = () => {
    if (!newFilterDescription.trim()) {
      alert("Please enter a description for the filter.");
      return;
    }
    const lastInputFilter = inputFilters[inputFilters.length - 1];
    const isLastFilterComplete = lastInputFilter && lastInputFilter.column && lastInputFilter.operator && lastInputFilter.value;
    let filtersToSave = [...filters];

    if (isLastFilterComplete) {
      const alreadyInConfirmedFilters = filters.some(f =>
        f.column === lastInputFilter.column &&
        f.operator === lastInputFilter.operator &&
        f.value === lastInputFilter.value
      );
      if (!alreadyInConfirmedFilters) {
        filtersToSave.push({
          column: lastInputFilter.column,
          operator: lastInputFilter.operator,
          value: lastInputFilter.value,
        });
      }
    }

    if (filtersToSave.length === 0) {
      alert("No filters to save.");
      return;
    }

    const result = saveFilter(newFilterDescription, filtersToSave, newFilterNotes);
    if (result) {
      setNewFilterDescription('');
      setNewFilterNotes('');
      setShowSaveFilterInput(false);
    } else {
      alert("Failed to save filter. Please check configuration or console for more details.");
    }
  };

  const handleApplySavedFilter = (savedFilterData) => {
    if (!savedFilterData || savedFilterData.length === 0) return;
    deleteCurrentFilter();
    const newFiltersState = savedFilterData.map(f => ({ ...f, id: uuidv4() }));
    setFilters(newFiltersState);



    const appliedInputFilters = newFiltersState.map(f => ({
      ...f,
      isMandatory: jsonColumns.find(col => col.id === f.column)?.isMandatory || false,
      maxValues: jsonColumns.find(col => col.id === f.column)?.maxValues,
    }));

    const finalInputFilters = [
      ...appliedInputFilters,
      { column: '', operator: '', value: '', id: uuidv4() }
    ];
    setInputFilters(finalInputFilters);

    const appliedColumns = new Set(newFiltersState.map(f => f.column));
    setColumnsToSelect(
      jsonColumns.map(col => ({
        ...col,
        selected: appliedColumns.has(col.id) || col.isMandatory,
      }))
    );

  };

  const handleDeleteSavedFilter = (filterId) => {
    deleteSavedFilterFromManager(filterId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const deleteCurrentFilter = () => {
    setFilters([]);
    setInputFilters([
      {
        column: '', operator: '', value: '', id: 0,
      },
    ]);
    setColumnsToSelect(jsonColumns);
    setFilterError('');
    setIsConfirmationModalOpen(false);
    setMessageType('');
    setUseProcessedTimestamp(false);
  };

  const commonInputStyle = "block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const commonLabelStyle = "block text-sm font-medium text-gray-700 mb-1";
  const commonButtonStyle = "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2";
  const primaryButtonStyle = `${commonButtonStyle} bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500`;
  const secondaryButtonStyle = `${commonButtonStyle} bg-pink-600 hover:bg-pink-700 focus:ring-pink-500`;

  const popupVariants = {
    hidden: { opacity: 0, scale: 0.95, y: "-10%" },
    visible: { opacity: 1, scale: 1, y: "0%", transition: { type: "spring", damping: 20, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.95, y: "-10%", transition: { duration: 0.2 } },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const filterBadgeVariants = {
    hidden: { opacity: 0, scale: 0.8, x: -10 },
    visible: { opacity: 1, scale: 1, x: 0, transition: { type: "spring", damping: 15, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.8, x: 10, transition: { duration: 0.2 } },
  };

  return (
    <div className="space-y-2 p-2">
      {subtitle && (
        <div>
          <label className="text-lg font-semibold text-gray-800">{subtitle}</label>
        </div>
      )}

      {filtersComponents.map((component) => (
        component.type === 'radio' && (
          <div key={component.label} className="my-4 p-4 border border-gray-200 rounded-md bg-white w-full md:w-1/4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{component.label}</label>
            <div role="radiogroup" className="space-y-2">
              {component.values.map((valueOption) => (
                <label key={valueOption.value} className="flex items-center space-x-2 text-sm text-gray-600">
                  <input
                    type="radio"
                    id={`btnRadio${id}${valueOption.label}`}
                    name={component.label}
                    value={valueOption.value}
                    defaultChecked={component.defaultValue === valueOption.value}
                    onChange={component.handleChange}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <span>{valueOption.label}</span>
                </label>
              ))}
            </div>
          </div>
        )
      ))}

      <div className="flex flex-col md:flex-row  items-start md:items-center space-y-4 md:space-y-0 md:space-x-4 ">
        <div className="w-full md:w-auto">
          <DateRangePicker
            initialStartDate={new Date()}
            initialEndDate={new Date()}
            onDateChange={handleDateChange}
            setSelectedStartDate={setSelectedStartDate}
            selectedStartDate={selectedStartDate}
            setSelectedStartTime={setSelectedStartTime}
            selectedStartTime={selectedStartTime}
            setSelectedEndDate={setSelectedEndDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndTime={setSelectedEndTime}
            selectedEndTime={selectedEndTime}
            datesOptional={datesOptional}
            maxDaysBetweenDates={maxDaysBetweenDates}
            setVisibility={setDateRangePickerVisibility}
            isEndDateEnabled={isEndDateEnabled}
            showNoEndateCheckbox={showNoEndateCheckbox}
          />
        </div>

        {filtersVisible && (
          <div className="relative mt-6">
            <button
              type="button"
              onClick={() => setIsPopupOpen(!isPopupOpen)}
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${filters.length > 0
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-transparent hover:from-indigo-600 hover:to-purple-700 focus:ring-indigo-500 shadow-lg transform hover:scale-105'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-indigo-500'
                }`}
            >
              <FunnelIcon className={`w-5 h-5 mr-2 ${filters.length > 0 ? 'text-white' : 'text-gray-400'}`} />
              Filters
              {filters.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-indigo-100 bg-white bg-opacity-20 rounded-full animate-pulse">
                  {filters.length}
                </span>
              )}
            </button>

            <AnimatePresence>
              {isPopupOpen && (
                <>
                  <motion.div
                    className="fixed inset-0 z-40 bg-gray-900 bg-opacity-5 "
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onClick={() => setIsPopupOpen(false)}
                  />
                  <motion.div
                    ref={popupRef}
                    variants={popupVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute left-1/2 transform -translate-x-1/2 z-50 mt-2 w-full lg:w-[768px] max-w-3xl rounded-md shadow-xl bg-white border border-gray-200"
                  >
                    <div className="flex justify-between items-center p-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">
                        Filters ({filters.length} {filters.length === 1 ? 'filter' : 'filters'} applied)
                      </h3>
                      <button
                        type="button"
                        onClick={handleClosePopup}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md"
                        aria-label="Close filters"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {/* Filter Added Confirmation */}
                    <AnimatePresence>
                      {showFilterAddedMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: -20, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -20, scale: 0.95 }}
                          className="mx-4 mt-2 p-3 bg-green-50 border border-green-200 rounded-md"
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-green-800">Filter added successfully!</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Applied Filters Section */}
                    {filters.length > 0 && (
                      <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                        <h4 className="text-sm font-semibold text-indigo-900 mb-2">Applied Filters:</h4>
                        <div className="flex flex-wrap gap-2">
                          <AnimatePresence>
                            {filters.map((filter, index) => (
                              <motion.div
                                key={`applied-${filter.column}-${filter.operator}-${index}`}
                                variants={filterBadgeVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm"
                              >
                                <span className="font-semibold">{jsonColumns.find(col => col.id === filter.column)?.label || filter.column}</span>
                                <span className="mx-1 opacity-75">{operators.find(op => op.value === filter.operator)?.label || filter.operator}</span>
                                <span className="font-semibold">
                                  {Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFilterCondition(filter)}
                                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                                  title="Remove filter"
                                >
                                  <XMarkIcon className="w-3 h-3" />
                                </button>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      </div>
                    )}

                    <div className="p-4 scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 overflow-y-auto max-h-[calc(100vh-200px)] md:max-h-[60vh]">
                      {jsonColumns?.length > 0 && inputFilters?.length > 0 && (
                        inputFilters.map((inputFilter, filterIndex) => {
                          const isAddedFilter = filterIndex !== inputFilters.length - 1 && inputFilter.column !== '';
                          const containerClasses = isAddedFilter
                            ? 'flex flex-col md:flex-row items-start md:items-end space-y-2 md:space-y-0 md:space-x-2 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg mb-3 shadow-sm'
                            : `flex flex-col md:flex-row items-start md:items-end space-y-2 md:space-y-0 md:space-x-2 py-3 ${filterIndex < inputFilters.length - 1 ? 'border-b border-gray-200' : ''}`;

                          const inputClasses = isAddedFilter
                            ? `${commonInputStyle} bg-green-50 border-green-300 text-green-800 font-medium`
                            : commonInputStyle;

                          const labelClasses = isAddedFilter
                            ? `${commonLabelStyle} text-green-700 font-semibold`
                            : commonLabelStyle;

                          return (
                            <div key={inputFilter.id} className={containerClasses}>

                              <div className="flex-grow w-full md:w-auto">
                                <label htmlFor={`column-${inputFilter.id}`} className={labelClasses}>Column</label>
                                <select
                                  id={`column-${inputFilter.id}`}
                                  value={inputFilter.column}
                                  onChange={(event) => handleColumnChange(event, inputFilter.id)}
                                  disabled={(inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== '') || inputFilter.isMandatory}
                                  className={inputClasses}
                                  data-testid={`input-column-${inputFilter.id}`}
                                >
                                  <option value="" disabled={inputFilter.column !== ''}>Select column</option>
                                  {columnsToSelect.map((c) => (
                                    <option key={c.id} value={c.id} data-testid={`input-column-${inputFilter.id}-${c.id}`}
                                      disabled={(filters.some(f => f.column === c.id) && c.id !== inputFilter.column)}>
                                      {c.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="flex-grow w-full md:w-auto">
                                <label htmlFor={`operator-${inputFilter.id}`} className={labelClasses}>Operator</label>
                                <select
                                  id={`operator-${inputFilter.id}`}
                                  value={inputFilter.operator}
                                  onChange={(e) => updateFilter('operator', e.target.value, inputFilter)}
                                  disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== '' || (inputFilter.column && jsonColumns.find((c) => c.id === inputFilter.column)?.valueType === valueTypes.STRICT_STRING_EQUAL)}
                                  className={inputClasses}
                                  data-testid={`input-operator-${inputFilter.id}`}
                                >
                                  <option value="" disabled>Select operator</option>
                                  {inputFilter.column && jsonColumns.find((c) => c.id === inputFilter.column)?.valueType === valueTypes.BOOLEAN && booleanOperators.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                  {inputFilter.column && jsonColumns.find((c) => c.id === inputFilter.column)?.valueType === valueTypes.STRING && stringOperators.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                  {inputFilter.column && jsonColumns.find((c) => c.id === inputFilter.column)?.valueType === valueTypes.STRICT_STRING_EQUAL && strictStringEqualOperators.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                  {inputFilter.column && ![valueTypes.BOOLEAN, valueTypes.STRING, valueTypes.STRICT_STRING_EQUAL].includes(jsonColumns.find((c) => c.id === inputFilter.column)?.valueType) && dateOperators.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="flex-grow w-full md:w-auto">
                                {inputFilter.column && (
                                  <label htmlFor={`value-${inputFilter.id}`} className={labelClasses}>
                                    {inputFilter.operator === 'IN' && !(jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.BOOLEAN || jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.LIST) ? 'Values (comma-separated)' : 'Value'}
                                  </label>
                                )}
                                {inputFilter.column && (jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.BOOLEAN || jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.LIST) && (
                                  <select
                                    id={`value-${inputFilter.id}`}
                                    value={inputFilter.value}
                                    onChange={(e) => updateFilter('value', e.target.value, inputFilter)}
                                    disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== ''}
                                    className={inputClasses}
                                    data-testid={`input-value-boolean-list-${inputFilter.id}`}
                                  >
                                    <option value="" disabled>Select value</option>
                                    {jsonColumns.find(c => c.id === inputFilter.column)?.values?.map(option => (
                                      <option key={option} value={option}>{option}</option>
                                    ))}
                                  </select>
                                )}
                                {inputFilter.column && jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.STRING && (
                                  <input
                                    type="text"
                                    id={`value-${inputFilter.id}`}
                                    value={inputFilter.value}
                                    onChange={(e) => updateFilter('value', e.target.value, inputFilter)}
                                    disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== ''}
                                    className={inputClasses}
                                    data-testid={`input-value-string-${inputFilter.id}`}
                                  />
                                )}
                                {inputFilter.column && jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.STRICT_STRING_EQUAL && (
                                  <input
                                    type="text"
                                    id={`value-${inputFilter.id}`}
                                    value={inputFilter.value}
                                    onChange={(e) => updateFilter('value', e.target.value, inputFilter)}
                                    disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== ''}
                                    className={inputClasses}
                                    data-testid={`input-value-strict-string-${inputFilter.id}`}
                                  />
                                )}
                                {inputFilter.column && jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.NUMBER && (
                                  <input
                                    type="text"
                                    id={`value-${inputFilter.id}`}
                                    value={inputFilter.value}
                                    onChange={(e) => updateFilter('value', e.target.value, inputFilter)}
                                    disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== ''}
                                    className={inputClasses}
                                    data-testid={`input-value-number-${inputFilter.id}`}
                                  />
                                )}
                                {inputFilter.column && jsonColumns.find(c => c.id === inputFilter.column)?.valueType === valueTypes.DATE && (
                                  <div className="relative">
                                    <input
                                      type="datetime-local"
                                      id={`value-${inputFilter.id}`}
                                      value={inputFilter.value}
                                      onChange={(e) => handleChangeDatePicker(e.target.value, inputFilter.id)}
                                      disabled={inputFilters?.length && filterIndex !== inputFilters.length - 1 && inputFilter.column !== ''}
                                      className={`${inputClasses} pr-10`}
                                      data-testid={`input-value-calendar-${inputFilter.id}`}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                      <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="flex items-end space-x-2 pt-2 md:pt-6">
                                {filterIndex === inputFilters.length - 1 && inputFilter.column !== '' && (
                                  <button
                                    type="button"
                                    onClick={() => addFilter(inputFilter)}
                                    disabled={selectorError !== '' || filterIsEmpty(inputFilter)}
                                    className={`${primaryButtonStyle} min-w-[80px] transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95`}
                                    data-testid="add-filter-button"
                                  >
                                    <PlusIcon className="h-5 w-5 mr-1" />
                                    ADD
                                  </button>
                                )}
                                {filterIndex !== inputFilters.length - 1 && inputFilter.column !== '' && !inputFilter.isMandatory && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-500 font-semibold hidden md:inline">AND</span>
                                    <button
                                      type="button"
                                      onClick={() => removeFilterCondition(inputFilter)}
                                      className={`${secondaryButtonStyle} min-w-[100px]`}
                                      data-testid="delete-filter-button"
                                    >
                                      <TrashIcon className="h-5 w-5 mr-1" />
                                      DELETE
                                    </button>
                                  </div>
                                )}
                              </div>
                              {selectorError && filterIndex === inputFilters.length - 1 && <p className="text-xs text-red-600 mt-1 w-full col-span-full">{selectorError}</p>}
                              {/* {!isDeviceAllowed && (inputFilter.column === 'device_serial' || inputFilter.column === 'serial') && filterError && filterIndex === inputFilters.length - 1 && (
                              <p className="text-xs text-red-600 mt-1 w-full col-span-full">{filterError}</p>
                            )} */}
                            </div>
                          );
                        })
                      )}
                      {filterError && !selectorError && <p className="text-sm text-red-600 p-2">{filterError}</p>}

                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-md font-semibold text-gray-800">Saved Filters</h4>
                          <button
                            type="button"
                            onClick={() => setShowSaveFilterInput(!showSaveFilterInput)}
                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium inline-flex items-center"
                          >
                            <BookmarkIcon className="w-5 h-5 mr-1" />
                            {showSaveFilterInput ? 'Cancel Save' : 'Save Current Filter'}
                          </button>
                        </div>

                        {showSaveFilterInput && (
                          <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                            <label htmlFor="newFilterDescription" className={`${commonLabelStyle} mb-1`}>Filter Description</label>
                            <input
                              type="text"
                              id="newFilterDescription"
                              value={newFilterDescription}
                              onChange={(e) => setNewFilterDescription(e.target.value)}
                              placeholder="e.g., My frequent search"
                              className={`${commonInputStyle} mb-2`}
                            />
                            <label htmlFor="newFilterNotes" className={`${commonLabelStyle} mb-1 mt-2`}>Notes (Optional)</label>
                            <textarea
                              id="newFilterNotes"
                              value={newFilterNotes}
                              onChange={(e) => setNewFilterNotes(e.target.value)}
                              placeholder="Add any notes for this filter"
                              className={`${commonInputStyle} mb-2 h-20 resize-none`}
                            />
                            <button
                              type="button"
                              onClick={handleSaveCurrentFilter}
                              disabled={!newFilterDescription.trim() || (filters.length === 0 && (inputFilters.length <= 1 || filterIsEmpty(inputFilters[inputFilters.length - 1])))}
                              className={`${primaryButtonStyle} w-full sm:w-auto disabled:opacity-50`}
                            >
                              Save Filter
                            </button>
                          </div>
                        )}

                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder="Search saved filters by description..."
                            value={savedFiltersSearchTerm}
                            onChange={(e) => setSavedFiltersSearchTerm(e.target.value)}
                            className={`${commonInputStyle} w-full`}
                          />
                        </div>

                        {savedFilters && savedFilters.length > 0 ? (
                          <div className="space-y-2 max-h-60 overflow-y-auto scrollbar scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
                            {savedFilters
                              .filter(sf => sf.description.toLowerCase().includes(savedFiltersSearchTerm.toLowerCase()))
                              .slice()
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((savedFilter) => (
                                <div key={savedFilter.id} className="p-3 bg-white border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="text-sm font-medium text-gray-800">{savedFilter.description}</p>
                                      <p className="text-xs text-gray-500">Saved: {formatDate(savedFilter.date)}</p>
                                      <p className="text-xs text-gray-500">Criteria: {savedFilter.data.length}</p>
                                      {savedFilter.notes && <p className="text-xs text-gray-500 mt-1">Notes: {savedFilter.notes}</p>}
                                    </div>
                                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                      <button
                                        type="button"
                                        onClick={() => handleApplySavedFilter(savedFilter.data)}
                                        className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        title="Apply this filter"
                                      >
                                        <ClipboardDocumentCheckIcon className="w-5 h-5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteSavedFilter(savedFilter.id)}
                                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        title="Delete this filter"
                                      >
                                        <TrashIcon className="w-5 h-5" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-3">No filters saved for this view yet.</p>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        <button
                          type="button"
                          onClick={deleteCurrentFilter}
                          className={`${secondaryButtonStyle} min-w-[100px]`}
                        >
                          <TrashIcon className="h-5 w-5 mr-1" />
                          DELETE CURRENT FILTER
                        </button>

                        <div className="flex justify-end space-x-3 p-4 mt-4 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={handleClosePopup}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Close
                          </button>
                          <SearchButton
                            id={`btn${id}PopupSearch`}
                            onClick={(e) => {
                              onSubmit(e, filters);
                              setIsPopupOpen(false);
                            }}
                            disabled={filters.length === 0 || (filterError !== '' && !filterError.includes("permission"))}
                          >
                            APPLY FILTERS
                          </SearchButton>
                        </div>
                      </div>


                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}

        {showTimestampToggle && (
          <label htmlFor="timestampToggle" className="flex items-center space-x-2 cursor-pointer ml-0 md:ml-4 mt-4 md:mt-0">
            <input
              type="checkbox"
              id="timestampToggle"
              checked={useProcessedTimestamp}
              onChange={handleTimestampToggleChange}
              className="rounded h-5 w-5 text-indigo-600 border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
            <span className="text-sm text-gray-700">
              {useProcessedTimestamp ? 'Using Processed Timestamp' : "Using Machine's Timestamp"}
            </span>
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {deviceSerialVisible && (
          <div className="space-y-1">
            <label htmlFor={`input${id}DeviceSerial`} className={commonLabelStyle}>Device Serial</label>
            <input
              type="text"
              id={`input${id}DeviceSerial`}
              value={deviceSerial}
              onChange={(event) => {
                if (setDeviceSerial) {
                  setDeviceSerial(event.target.value);
                }
              }}
              className={`${commonInputStyle} ${deviceSerialError ? 'border-red-500' : ''}`}
            />
            {deviceSerialError && <p className="text-xs text-red-600 mt-1">Cannot be empty</p>}
            {/* {!isDeviceAllowed && deviceToCheck && fieldToAddInTheFilter === 'device_serial' && <p className="text-xs text-red-600 mt-1">You are not allowed to add this device</p>} */}
          </div>
        )}
        {messageTypeVisible && (
          <div className="space-y-1">
            <label htmlFor={`input${id}MessageType`} className={commonLabelStyle}>Message type</label>
            <input
              type="text"
              id={`input${id}MessageType`}
              value={messageType}
              onChange={(event) => {
                setMessageType(event.target.value);
              }}
              className={commonInputStyle}
            />
          </div>
        )}
      </div>

      <div className={`flex items-center mt-6 ${linkVisible ? 'justify-between' : 'justify-end'}`}>
        {linkVisible && (
          <a
            href={linkURL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            {linkText}
          </a>
        )}
        <SearchButton
          id={`btn${id}Search`}
          onClick={(e) => onSubmit(e, filters)}
          disabled={filterError !== '' && !filterError.includes("permission")}
        >
          SEARCH
        </SearchButton>
      </div>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCancelApplyFilters}
        onConfirm={handleModalConfirm}
        title={modalTitle}
        message={modalMessage}
        confirmText={modalConfirmText}
        cancelText={modalCancelText}
      />
    </div>
  );
}


JSONBuilderFilters.defaultProps = {
  selectedStartDate: null,
  selectedStartTime: '',
  selectedEndDate: null,
  selectedEndTime: '',
  linkVisible: false,
  linkText: '',
  linkURL: '',
  messageTypeVisible: false,
  datesOptional: true,
  deviceSerial: '',
  setDeviceSerial: () => { },
  messageType: '',
  setMessageType: () => { },
  deviceSerialError: false,
  maxDaysBetweenDates: 31,
  subtitle: '',
  filtersComponents: [],
  setDateRangePickerVisibility: () => { },
  setFilterError: () => { },
  filterError: '',
  onSearch: () => { },
  isEndDateEnabled: true,
  showNoEndateCheckbox: true,
  useProcessedTimestamp: false,
  setUseProcessedTimestamp: () => { },
  showTimestampToggle: false,
};
